import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import { Platform } from "@-/com.npmjs.react-native";
import {
  cancelAnimation,
  Easing,
  runOnJS,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useMemo, useRef } from "react";
import {
  resistTopSheetOverdrag,
  resistGorhomSheetOverdrag,
  gorhomSheetDragOrigin,
  gorhomSheetSnapPosition,
  topSheetDragOrigin,
  topSheetSnapPosition,
  topSheetSnapHeight,
} from "#top-sheet-physics";

const SHEET_SPRING = { damping: 28, mass: 0.8, stiffness: 260 } as const;
// Match @gorhom/bottom-sheet's platform defaults; keep the existing preset for other callers.
const GORHOM_SPRING = {
  damping: 500,
  mass: 3,
  overshootClamping: true,
  restDisplacementThreshold: 10,
  restSpeedThreshold: 10,
  stiffness: 1000,
} as const;
const IS_ANDROID = Platform.OS === "android";

function animateSheet(
  target: number,
  velocity: number,
  preset: "elastic" | "gorhom",
  onComplete: (finished?: boolean) => void,
) {
  "worklet";
  return preset === "gorhom" && IS_ANDROID
    ? withTiming(target, { duration: 250, easing: Easing.out(Easing.exp) }, onComplete)
    : withSpring(
        target,
        {
          ...(preset === "gorhom" ? GORHOM_SPRING : SHEET_SPRING),
          velocity: preset === "gorhom" ? velocity / 2 : 0,
        },
        onComplete,
      );
}

/** Shared UI-thread motion for resizing sheets and sliding window-level sheets. */
export function useTopSheetMotion({
  position,
  initialPosition = position,
  maximum,
  minimum = 0,
  motionPreset = "elastic",
  onDragStart,
  onPositionChange,
  onSettled,
  scrollOffset,
  snapInterval = 0,
  snapOffset,
  snapPoints,
  snapThreshold = 0.5,
  snapToMinimum = false,
}: {
  initialPosition?: number;
  maximum: number;
  minimum?: number;
  motionPreset?: "elastic" | "gorhom";
  onDragStart?: () => void;
  onPositionChange: (position: number) => void;
  onSettled?: (position: number) => void;
  position: number;
  /** Enables downward content pulls at scroll offset zero; upward swipes keep scrolling. */
  scrollOffset?: SharedValue<number>;
  /** Continues the last snap point at this spacing, past the end of content that ends early. */
  snapInterval?: number;
  /** Scroll position subtracted from content-relative snap boundaries at release. */
  snapOffset?: SharedValue<number>;
  /** Optional content boundaries; position is normalized between minimum and maximum. */
  snapPoints?: readonly number[];
  snapThreshold?: number;
  /** Keeps the minimum a resting position beside `snapPoints`, so the sheet can always close. */
  snapToMinimum?: boolean;
}) {
  const callbacks = useLatest({ onDragStart, onPositionChange, onSettled });
  const bounds = useSharedValue({
    maximum: Math.max(minimum, maximum),
    minimum,
    snapInterval,
    snapPoints,
    snapToMinimum,
  });
  const initialHeight = minimum + (Math.max(minimum, maximum) - minimum) * initialPosition;
  const value = useSharedValue(
    snapPoints === undefined
      ? initialHeight
      : topSheetSnapHeight(
          initialHeight,
          0,
          minimum,
          maximum,
          snapPoints,
          snapOffset?.get(),
          0,
          snapInterval,
          snapToMinimum,
        ),
  );
  const dragStart = useSharedValue(0);
  const dragPosition = useSharedValue(value.get());
  const dragActive = useSharedValue(false);
  const animatedTarget = useSharedValue(-1);
  const touchStart = useSharedValue({ x: 0, y: 0 });
  const progress = useDerivedValue(() => {
    const { maximum: max, minimum: min } = bounds.get();
    return (value.get() - min) / Math.max(1, max - min);
  });
  const renderedBounds = useRef<{ maximum: number; minimum: number } | null>(null);
  const reportSettled = useMemo(
    () => (next: number) => callbacks.current.onSettled?.(next),
    [callbacks],
  );

  useEffect(() => {
    // A JS-thread `set` reaches the UI runtime asynchronously, so `bounds.get()` here can still read
    // older bounds: a sheet whose maximum grows on layout would rest at a height scaled by the old
    // maximum. Compare and size with the bounds React rendered instead.
    const previousBounds = renderedBounds.current;
    const nextMaximum = Math.max(minimum, maximum);
    renderedBounds.current = { maximum: nextMaximum, minimum };
    bounds.set({
      maximum: nextMaximum,
      minimum,
      snapInterval,
      snapPoints,
      snapToMinimum,
    });
    if (!dragActive.get()) {
      const next = Math.max(0, Math.min(1, position));
      // React observes the release after the UI-thread spring has started. Keep its velocity.
      // New snap points alone never move the sheet: content boundaries only apply when a drag
      // releases, so replacing the content keeps the height the user chose.
      if (
        animatedTarget.get() === next &&
        previousBounds?.maximum === nextMaximum &&
        previousBounds.minimum === minimum
      ) {
        return;
      }
      animatedTarget.set(next);
      const height = minimum + (nextMaximum - minimum) * next;
      const target =
        snapPoints === undefined
          ? height
          : topSheetSnapHeight(
              height,
              0,
              minimum,
              nextMaximum,
              snapPoints,
              snapOffset?.get(),
              0,
              snapInterval,
              snapToMinimum,
            );
      value.set(
        animateSheet(target, 0, motionPreset, (finished) => {
          "worklet";
          if (finished) {
            runOnJS(reportSettled)(next);
          }
        }),
      );
    }
  }, [
    animatedTarget,
    bounds,
    dragActive,
    maximum,
    minimum,
    motionPreset,
    position,
    reportSettled,
    snapInterval,
    snapOffset,
    snapPoints,
    snapToMinimum,
    value,
  ]);

  const gestures = useMemo(() => {
    const reportStart = () => callbacks.current.onDragStart?.();
    const reportPosition = (next: number) => callbacks.current.onPositionChange(next);
    const settle = (velocity: number) => {
      "worklet";
      const {
        maximum: max,
        minimum: min,
        snapInterval: interval,
        snapPoints: points,
        snapToMinimum: includeMinimum,
      } = bounds.get();
      const edge =
        motionPreset === "gorhom"
          ? gorhomSheetSnapPosition(dragPosition.get(), velocity, min, max)
          : topSheetSnapPosition(value.get(), velocity, min, max, snapThreshold);
      const target =
        points === undefined
          ? edge === 1
            ? max
            : min
          : topSheetSnapHeight(
              value.get(),
              velocity,
              min,
              max,
              points,
              snapOffset?.get(),
              motionPreset === "gorhom" ? 0.2 : 0.12,
              interval,
              includeMinimum,
            );
      const next = points === undefined ? edge : (target - min) / Math.max(1, max - min);
      dragActive.set(false);
      animatedTarget.set(next);
      value.set(
        animateSheet(target, velocity, motionPreset, (finished) => {
          "worklet";
          if (finished) {
            runOnJS(reportSettled)(next);
          }
        }),
      );
      runOnJS(reportPosition)(next);
    };
    const createDrag = () =>
      Gesture.Pan()
        .onStart(() => {
          "worklet";
          dragActive.set(true);
          animatedTarget.set(-1);
          cancelAnimation(value);
          const { maximum: max, minimum: min } = bounds.get();
          const origin =
            motionPreset === "gorhom"
              ? gorhomSheetDragOrigin(value.get(), min, max)
              : topSheetDragOrigin(value.get(), min, max);
          dragStart.set(origin);
          dragPosition.set(origin);
          runOnJS(reportStart)();
        })
        .onUpdate(({ translationY }) => {
          "worklet";
          const { maximum: max, minimum: min } = bounds.get();
          const raw = dragStart.get() + translationY;
          dragPosition.set(raw);
          value.set(
            motionPreset === "gorhom"
              ? resistGorhomSheetOverdrag(raw, min, max)
              : resistTopSheetOverdrag(raw, min, max),
          );
        })
        .onEnd(({ velocityY }) => {
          "worklet";
          settle(velocityY);
        })
        .onFinalize((_event, success) => {
          "worklet";
          if (!success && dragActive.get()) {
            settle(0);
          }
          dragActive.set(false);
        });
    // A drag that starts on the handle stays with the sheet in every direction: it claims sideways
    // travel before an enclosing pager can, so a diagonal pull never turns the page.
    const handle = createDrag().activeOffsetX([-4, 4]).activeOffsetY([-4, 4]);
    const content = createDrag()
      .manualActivation(true)
      .onTouchesDown((event) => {
        "worklet";
        touchStart.set({
          x: event.allTouches[0]?.absoluteX ?? 0,
          y: event.allTouches[0]?.absoluteY ?? 0,
        });
      })
      .onTouchesMove((event, manager) => {
        "worklet";
        if (dragActive.get()) {
          return;
        }
        const touch = event.allTouches[0];
        if (touch === undefined || scrollOffset === undefined || scrollOffset.get() > 0) {
          manager.fail();
          return;
        }
        const deltaX = Math.abs(touch.absoluteX - touchStart.get().x);
        const deltaY = touch.absoluteY - touchStart.get().y;
        if (deltaX > 20 || deltaY < -4) {
          manager.fail();
        } else if (deltaY > 4 && deltaY > deltaX) {
          manager.activate();
        }
      });
    return { content: Gesture.Simultaneous(content, Gesture.Native()), handle };
  }, [
    animatedTarget,
    bounds,
    callbacks,
    dragActive,
    dragPosition,
    dragStart,
    motionPreset,
    reportSettled,
    scrollOffset,
    snapOffset,
    snapThreshold,
    touchStart,
    value,
  ]);

  return { ...gestures, contentDraggable: scrollOffset !== undefined, progress, value };
}
